import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "../../../../components/Common/Button";
import {analyticsEvents, softSkillsDataset} from "../../../../db/models/const";
import {
    AssessmentNotPaginatedInterface,
    AssessmentQuestionInterface,
    AssessmentType,
} from "../../../../db/models/assessment";
import {StudentChartSoftSkillsType} from "../../../../db/models/student/cv";
import {StudentSoftSkillAnswerInterface} from "../../../../db/models/student/softSkill";
import {SoftSkillTagType} from "../../../../db/models/softSkill";
import {getAssessment} from "../../../../api/assessment";
import {addStudentSoftSkills} from "../../../../api/student/softSkills";
import {calculateSoftSkills} from "../../../../helpers/misc";
import "./style.scss";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ButtonCircle from "../../../../components/Common/ButtonCircle";
import {Card, Col, Placeholder, Row} from "react-bootstrap";

interface Props {
    step?: number;
    assessmentId?: string;
    handleAssessmentResult?: Function; // from parent component in private onboarding flow, used to show the emoji
    storeAnswers?: Boolean;
    onFinish?: Function; // used on assessment completion, for side effects like saving answers to db
}

export const StudentOnboardingAssessment: React.FC<Props> = ({
                                                                 step,
                                                                 assessmentId,
                                                                 handleAssessmentResult,
                                                                 storeAnswers = true,
                                                                 onFinish,
                                                             }) => {
    const [questions, setQuestions] = useState<AssessmentQuestionInterface[]>([]);
    const [question, setQuestion] = useState<AssessmentQuestionInterface>();
    const [questionTags, setQuestionTags] = useState<SoftSkillTagType[]>([]);
    const [answers, setAnswers] = useState<StudentSoftSkillAnswerInterface[]>([]);
    const [questionIndex, setQuestionIndex] = useState<number>(0);
    const [loading, setLoading] = useState(true);
    const [partialCounter, setPartialCounter] = useState<number>(1);
    const [totalCounter, setTotalCounter] = useState<number>(0);
    const {t} = useTranslation();

    // get assessment by assessmentId
    useEffect(() => {
        if (!assessmentId) return;
        getAssessment(assessmentId).then((assessment: AssessmentType | null) => {
            console.log(assessment);

            const assessmentNotPaginated = {
                ...assessment,
            } as AssessmentNotPaginatedInterface;

            const questions =
                assessmentNotPaginated.questions.sort((a, b) => {
                    if (a.tag < b.tag) return -1;
                    else return 1;
                }) || [];
            setQuestions(questions);

            const firstQuestion = questions?.[0] || null;
            const {totalCounter, partialCounter} = computeCounters(
                questions,
                firstQuestion
            );
            setPartialCounter(partialCounter);
            setTotalCounter(totalCounter);
            setQuestion(firstQuestion);
            setQuestionIndex(0);
            const questionTags = [...new Set(questions.map((q) => q.tag))];
            setQuestionTags(questionTags);
            setLoading(false);
        });
    }, [assessmentId]);

    // compute soft skills values
    const finishAssessment = () => {
        if (!assessmentId) return;

        if (storeAnswers) {
            setLoading(true);
            addStudentSoftSkills(answers, assessmentId).then(() => {

                // compute higher soft skill
                const softSkills: StudentChartSoftSkillsType = calculateSoftSkills(
                    answers
                ) as StudentChartSoftSkillsType;
                const maxSoftValue = Math.max(...Object.values(softSkills));
                const maxSoftSkillTag: SoftSkillTagType = Object.entries(
                    softSkills
                ).find((e) => e[1] === maxSoftValue)?.[0] as SoftSkillTagType;

                if (handleAssessmentResult) handleAssessmentResult(maxSoftSkillTag);
            });
        }
        if (onFinish) onFinish(answers, assessmentId);
        return;
    };

    // save current question answers and set next question
    const onNextQuestion = async (value) => {
        if (!question) return;
        const answer: StudentSoftSkillAnswerInterface = {
            qId: question.id,
            tag: question.tag,
            value,
        };
        setAnswers([...answers, answer]);

        if (questionIndex < questions.length - 1) {
            // go to next question
            const newQuestionIndex = questionIndex + 1;
            setQuestionIndex(newQuestionIndex);
            const newQuestion = questions[newQuestionIndex];
            setQuestion(newQuestion);

            const {partialCounter, totalCounter} = computeCounters(
                questions,
                newQuestion
            );
            setPartialCounter(partialCounter);
            setTotalCounter(totalCounter);
        } else {
            // end of the assessment, save answers to db
            finishAssessment();
        }
    };

    const computeCounters = (
        questions: AssessmentQuestionInterface[],
        question: AssessmentQuestionInterface
    ) => {
        const tagQuestions = questions.filter((q) => q.tag === question.tag);
        const totalCounter = tagQuestions.length;
        const partialCounter =
            tagQuestions.findIndex((q) => q.id === question.id) + 1;
        return {totalCounter, partialCounter};
    };

    // set previous question
    const onPreviousQuestion = () => {
        const newQuestionIndex = questionIndex - 1;
        const newQuestion = questions[newQuestionIndex];
        setQuestionIndex(newQuestionIndex);
        setQuestion(newQuestion);

        const {partialCounter, totalCounter} = computeCounters(
            questions,
            newQuestion
        );
        setPartialCounter(partialCounter);
        setTotalCounter(totalCounter);

        setAnswers([...answers.slice(0, -1)]);
    };

    return (
        <div className="studentOnboardingAssessment">
            {loading && (
                <div className={"mnw-200 text-center"}>
                    <Row>
                        <Col sm={12} md={6}>
                            <div className={"h-100 px-3 py-5 bg-grey circle_placeholder"}>
                                <Placeholder as={Card.Title} animation="glow">
                                    <Placeholder xs={4} size={"lg"}/>
                                </Placeholder>
                                <Placeholder xs={11} bg="info" style={{minHeight: "0.4rem"}}/>
                                <Placeholder as={Card.Text} animation="glow">
                                    <Placeholder xs={7}/> <Placeholder xs={4}/> <Placeholder xs={4}/>{' '}
                                    <Placeholder xs={6}/> <Placeholder xs={8}/>
                                </Placeholder>
                            </div>
                        </Col>
                        <Col>
                            <div className={"mt-4"}>
                                <Placeholder as={Card.Text} animation="glow">
                                    <Placeholder className={"circle_placeholder py-4 my-2"} xs={8} size={"lg"}
                                                 bg="primary"/>
                                    <Placeholder className={"circle_placeholder py-4 my-2"} xs={8} size={"lg"}
                                                 bg="primary"/>
                                    <Placeholder className={"circle_placeholder py-4 my-2"} xs={8} size={"lg"}
                                                 bg="primary"/>
                                    <Placeholder className={"circle_placeholder py-4 my-2"} xs={8} size={"lg"}
                                                 bg="primary"/>
                                </Placeholder>
                            </div>
                            <div className={"mt-4"}>
                                <Placeholder.Button className={"circle_placeholder p-4 my-2 mx-2"}
                                                    variant="primary" xs={3} size={"lg"} animation="glow"/>
                                <Placeholder.Button className={"circle_placeholder p-4 my-2"}
                                                    variant="primary" xs={8} size={"lg"} animation="glow"/>
                            </div>
                        </Col>
                    </Row>
                </div>
            )}
            {!loading && (
                <AssessmentQuestion
                    question={question}
                    onNextQuestion={onNextQuestion}
                    onPreviousQuestion={onPreviousQuestion}
                    totalCounter={totalCounter}
                    partialCounter={partialCounter}
                    questionIndex={questionIndex}
                    questionTags={questionTags}
                />
            )}
        </div>
    );
};

export default StudentOnboardingAssessment;

interface AssessmentQuestionPropsInterface {
    question: AssessmentQuestionInterface | undefined;
    onNextQuestion: Function;
    onPreviousQuestion: Function;
    totalCounter: number;
    partialCounter: number;
    questionIndex: number;
    questionTags: SoftSkillTagType[];
}

const AssessmentQuestion: React.FC<AssessmentQuestionPropsInterface> = ({
                                                                            question,
                                                                            onNextQuestion,
                                                                            onPreviousQuestion,
                                                                            totalCounter,
                                                                            partialCounter,
                                                                            questionIndex,
                                                                            questionTags,
                                                                        }) => {
    const {t} = useTranslation();
    const tagIcons = questionTags.map((tag) => {
        return {tag, icon: softSkillsDataset[tag].icon};
    });
    const [value, setValue] = useState(0);

    const onAnswer = (value) => {
        setValue(value);
    };

    const handleNextQuestion = () => {
        onNextQuestion(value);
        setValue(0);
    };

    return (
        <div className="assessmentQuestion">
            <div className="assessmentQuestion__card card">
                <div className="assessmentQuestion__counter">
					<span className="assessmentQuestion__counterPartial">
						{partialCounter.toLocaleString("en-US", {
                            minimumIntegerDigits: 2,
                            useGrouping: false,
                        })}
					</span>
                    <span className="assessmentQuestion__counterTotal">
						{"/"}
                        {totalCounter.toLocaleString("en-US", {
                            minimumIntegerDigits: 2,
                            useGrouping: false,
                        })}
					</span>
                </div>
                <div className="assessmentQuestion__icons">
                    {tagIcons.map((item) => (
                        <div
                            key={item.tag}
                            className={
                                item.tag === question?.tag
                                    ? "assessmentQuestion__icon assessmentQuestion__icon--active"
                                    : "assessmentQuestion__icon"
                            }
                        >
                            <item.icon/>
                        </div>
                    ))}
                </div>
                <div className="assessmentQuestion__progressBar"></div>
                <div className="assessmentQuestion__question">
                    <p>{question?.text}</p>
                </div>
            </div>
            <div className="assessmentQuestion__buttons">
                <div className="assessmentQuestion__answerOptions">
                    <Button onClick={() => onAnswer(1)} filled={value === 1}>
                        {t("student_onboarding_softSkill_answer_never")}
                    </Button>
                    <Button onClick={() => onAnswer(2)} filled={value === 2}>
                        {t("student_onboarding_softSkill_answer_sometimes")}
                    </Button>
                    <Button onClick={() => onAnswer(3)} filled={value === 3}>
                        {t("student_onboarding_softSkill_answer_often")}
                    </Button>
                    <Button onClick={() => onAnswer(4)} filled={value === 4}>
                        {t("student_onboarding_softSkill_answer_always")}
                    </Button>
                </div>
                <div className="assessmentQuestion__moveButtons">
                    {questionIndex > 0 && (
                        <div className="assessmentQuestion__backBtn">
                            <ButtonCircle onClick={() => onPreviousQuestion()}>
                                <ArrowBackIcon/>
                            </ButtonCircle>
                        </div>
                    )}
                    <Button
                        className="assessmentQuestion__nextBtn"
                        filled
                        onClick={() => handleNextQuestion()}
                        disabled={value < 1 || value > 4}
                    >
                        {t("student_onboarding_softSkill_answer_nextBtn")}
                    </Button>
                </div>
            </div>
        </div>
    );
};
